import { Injectable, inject } from '@angular/core';
import { AuthService } from 'src/app/shared/services/auth.service';
import { log } from '../utils/logger';

export interface FilterPreferences {
  values: Record<string, unknown>;
  activeAdditionalFilters?: string[];
  lastUpdated?: number;
  /**
   * Written to localStorage so we never migrate anonymous legacy blobs
   * into a user’s scoped key unless they match.
   */
  ownerUserId?: string;
  /** Mirrors {@link AuthService.filterPreferencesContextKey} when saved. */
  preferencesContextKey?: string;
}

const logger = log.getLogger('filter-preferences.service');

@Injectable({
  providedIn: 'root',
})
export class FilterPreferencesService {
  private readonly STORAGE_PREFIX = 'filter_preferences_';
  private readonly authService = inject(AuthService);

  private currentUserId(): string | null {
    return this.authService.user?.uid ?? null;
  }

  preferencesSessionToken(filterKey: string): string | null {
    const uid = this.currentUserId();
    if (!uid) {
      return null;
    }
    const ctx = this.authService.filterPreferencesContextKey();
    return `${filterKey}::${uid}::${ctx}`;
  }

  savePreferences(filterKey: string, preferences: FilterPreferences): void {
    if (!filterKey?.trim()) {
      logger.warn('savePreferences called with invalid filterKey:', filterKey);
      return;
    }
    const uid = this.currentUserId();
    if (!uid) {
      return;
    }
    try {
      const storageKey = this.getStorageKey(filterKey, uid);
      const payload = this.toPersistedPayload(
        { ...preferences, lastUpdated: Date.now() },
        uid,
        this.authService.filterPreferencesContextKey()
      );
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      logger.error(`Error saving filter preferences for ${filterKey}:`, error);
    }
  }

  getPreferences(filterKey: string): FilterPreferences | null {
    const uid = this.currentUserId();
    if (!uid) {
      return null;
    }
    try {
      const storageKey = this.getStorageKey(filterKey, uid);
      const scoped = localStorage.getItem(storageKey);
      if (scoped) {
        return this.parseStored(scoped);
      }

      const migrated = this.migrateLegacyFilterPreferences(
        filterKey,
        uid,
        storageKey
      );
      if (migrated) {
        return migrated;
      }
    } catch (error) {
      logger.error(`Error loading filter preferences for ${filterKey}:`, error);
    }
    return null;
  }

  private legacyPayloadOwnedByUser(raw: string, uid: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
    if (Array.isArray(parsed)) {
      return false;
    }
    if (parsed && typeof parsed === 'object' && 'ownerUserId' in parsed) {
      return (parsed as { ownerUserId: unknown }).ownerUserId === uid;
    }
    return false;
  }

  private toPersistedPayload(
    preferences: FilterPreferences,
    ownerUid: string,
    preferencesContextKey: string
  ): FilterPreferences {
    return {
      values: preferences.values,
      activeAdditionalFilters: preferences.activeAdditionalFilters,
      lastUpdated: preferences.lastUpdated,
      ownerUserId: ownerUid,
      preferencesContextKey,
    };
  }

  clearPreferences(filterKey: string): void {
    const uid = this.currentUserId();
    try {
      if (uid) {
        const storageKey = this.getStorageKey(filterKey, uid);
        localStorage.removeItem(storageKey);
      }
      this.removeLegacySharedKeys(filterKey);
    } catch (error) {
      logger.error(
        `Error clearing filter preferences for ${filterKey}:`,
        error
      );
    }
  }

  private parseStored(raw: string): FilterPreferences | null {
    const parsed = JSON.parse(raw);

    // Legacy format: plain array of active filter names
    if (Array.isArray(parsed)) {
      return {
        activeAdditionalFilters: parsed,
        values: {},
      };
    }

    // Object format (handles both { active, values } and
    // { activeAdditionalFilters, values, lastUpdated })
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        values: parsed.values ?? {},
        activeAdditionalFilters:
          parsed.activeAdditionalFilters ??
          (parsed as { active?: string[] }).active ??
          [],
        lastUpdated: (parsed as { lastUpdated?: number }).lastUpdated,
        ownerUserId: (parsed as { ownerUserId?: string }).ownerUserId,
        preferencesContextKey: (parsed as { preferencesContextKey?: string })
          .preferencesContextKey,
      };
    }

    return null;
  }

  private getStorageKey(filterKey: string, uid: string): string {
    const ctx = encodeURIComponent(
      this.authService.filterPreferencesContextKey()
    );
    return `${this.STORAGE_PREFIX}${filterKey}__u_${encodeURIComponent(uid)}__c_${ctx}`;
  }

  private migrateLegacyFilterPreferences(
    filterKey: string,
    uid: string,
    scopedStorageKey: string
  ): FilterPreferences | null {
    const legacyKeys = [
      `${this.STORAGE_PREFIX}${filterKey}`,
      filterKey,
    ] as const;

    for (const legacyKey of legacyKeys) {
      const raw = localStorage.getItem(legacyKey);
      if (!raw) {
        continue;
      }
      if (!this.legacyPayloadOwnedByUser(raw, uid)) {
        this.removeLegacySharedKeys(filterKey);
        return null;
      }
      const parsed = this.parseStored(raw);
      if (!parsed) {
        localStorage.removeItem(legacyKey);
        continue;
      }
      try {
        const preferencesWithTimestamp: FilterPreferences = {
          ...parsed,
          lastUpdated: parsed.lastUpdated ?? Date.now(),
        };
        localStorage.setItem(
          scopedStorageKey,
          JSON.stringify(
            this.toPersistedPayload(
              preferencesWithTimestamp,
              uid,
              this.authService.filterPreferencesContextKey()
            )
          )
        );
      } catch (error) {
        logger.error(
          `Error migrating filter preferences for ${filterKey}:`,
          error
        );
        this.removeLegacySharedKeys(filterKey);
        return null;
      }
      this.removeLegacySharedKeys(filterKey);
      return parsed;
    }
    return null;
  }

  /** Pre-user-scoped keys were shared across accounts on the same browser. */
  private removeLegacySharedKeys(filterKey: string): void {
    localStorage.removeItem(`${this.STORAGE_PREFIX}${filterKey}`);
    localStorage.removeItem(filterKey);
  }
}
